import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import BenefitCard from './BenefitCard/BenefitCard';
import Button from './Button/Button';
import DeclarationHeading from './DeclarationHeading/DeclarationHeading';
import Embed from './Embed/Embed';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import InlineCategoryContent from './InlineCategoryContent/InlineCategoryContent';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Quote from './Quote/Quote';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import Stockists from './Stockists/Stockists';
import Table from './Table/Table';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';
import { flatten } from '../../utils/array';
import { percentToColumn } from '../../utils/columns';
import { purifier } from '../../utils/dom';
import { deepMerge } from '../../utils/object';
import { titleCase, urlify, camelCase, cleanInternalLinks } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';
import s from './TemplateParser.scss';

const components = {
	acfBenefitCard: BenefitCard,
	acfDeclaration: DeclarationHeading,
	acfInlineCategoryContent: InlineCategoryContent,
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfStockists: Stockists,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreTable: Table,
	coreVideo: Video,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[`container__${template}`],
			...attrsClassName.map((cN) => s[`container${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs?.layout?.justifyContent && attrs.layout.justifyContent !== 'full') generalProps.style.justifyContent = attrs.layout.justifyContent;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = 'u_fullWidth';

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (attrs) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs?.href) {
		const cleanLink = cleanInternalLinks(attrs.href);
		const externalLink = /^(https?|mailto|tel)/.test(cleanLink);
		const anchorLink = /^#/.test(cleanLink);

		if (attrs.target || externalLink || anchorLink) {
			Element = 'a';
			extraProps.href = attrs.href;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = cleanLink.replace(/\/+$/, '');
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup, useComponents = true) => {
	if (isRestricted(setup, attrs)) return null;
	if (components[template]) {
		if (useComponents) return components[template];
		return NoContainer;
	}
	if (containers[template]) return containers[template];

	if (['ccBlocksMeta'].indexOf(template) === -1) console.log('DEBUG: missing template', template, containers, components);
	return null;
};

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

containers.acfBenefits = ({ attrs, content, setup, template }) => {
	const customProps = {};
	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					let tpl = Object.keys(childSection)[0];
					let nextContent = childSection[tpl];
					if (tpl === 'content') {
						nextContent = { content: childSection[tpl] };
						tpl = 'acfBenefitCard';
					}
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} />;
				})
			}
		</div>
	);
};

containers.acfCategory = ({ content, setup, inlineCategoryContent }) => (
	<>
		{
			content.map((childSection) => {
				let tpl = Object.keys(childSection)[0];
				const nextContent = childSection[tpl];
				if (tpl === 'content') tpl = 'acfInlineCategoryContent';
				const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
				if (!ChildElement) return null;
				return <ChildElement template={tpl} key={nextContent.key} setup={setup} inlineCategoryContent={inlineCategoryContent} {...nextContent} />;
			})
		}
	</>
);

containers.ccBlocksAccordion = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div id={urlify(attrs.title.slice(0, 50))} {...extraProps}>
			<div className={s.container_content}>
				<span className={s.container_content_title}>{ attrs.title }</span>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} isAccordianContent {...nextContent} {...props} />;
					})
				}
			</div>
		</div>
	);
};

containers.ccBlocksPuff = ({ attrs, content, uid, setup, template, ...props }) => {
	const [customProps, settings] = heightSettings({ attrs, setup });

	const [Element, elementProps] = getElement(attrs);
	customProps.className = [s[`container__${camelCase(attrs.layout || 'center Center')}`]];
	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} />
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

containers.ccBlocksSlider = ({ attrs, content, setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	const autoplayDelay = content.filter((childSection, i) => {
		if (i !== 0) return false;
		const tpl = Object.keys(childSection)[0];
		if (tpl !== 'ccBlocksPuff') return false;
		const backgroundImage = childSection[tpl]?.attrs?.image?.url;
		if (/\.mp4$/.test(backgroundImage)) return true;
		return false;
	});

	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite: attrs?.infinite || true,
		autoplay: attrs?.transitionDelay || 3000,
		autoplayDelay: autoplayDelay.length > 0,
		interactionTypes: [],
		startIndex: 1,
	};
	let Wrapper;

	const extraProps = generalSettings({ customProps, attrs, template });

	if (attrs?.freeFlow && attrs?.align === 'full') {
		Wrapper = ({ children }) => (
			<div className={s.container__ccBlocksSlider_inner}>
				<button className={s.container__ccBlocksSlider_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
					<svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L11 9L1 17" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	} else {
		Wrapper = ({ children }) => (
			<Slider {...sliderAttrs}>
				{ children }
			</Slider>
		);
	}

	return (
		<div {...extraProps}>
			<Wrapper>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</Wrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
	};
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} />;

	const extraProps = generalSettings({ setup, customProps, attrs, template });
	let Wrapper = ({ children }) => (
		<ul {...extraProps}>
			{ children }
		</ul>
	);

	if (attrs?.ordered) {
		Wrapper = ({ children }) => (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreQuote = ({ attrs = {}, content = {}, setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content.quote)) return <Quote content={content} />;

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<blockquote {...extraProps}>
			<div className={s.container__coreQuote_content}>
				{
					content.quote.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
			{ content.cite && <cite className={s.container__coreQuote_cite} dangerouslySetInnerHTML={{ __html: purifier(content.cite.rawText, { ADD_ATTR: ['target'] }) }} /> }
		</blockquote>
	);
};

containers.coreColumn = ({ attrs, content, setup, template, column, ...props }) => {
	const customProps = {
		className: [],
	};

	if (attrs?.width && attrs.width.indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { width: attrs.width };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];
	if (!attrs?.width) customProps.className.push('u_width__grow');

	const extraProps = generalSettings({ customProps, attrs, template });

	let Wrapper = ({ children }) => children;
	if (!props?.useFluidContent || (props?.useFluidContent && setup.breakPoint !== 'mobile')) {
		Wrapper = ({ children }) => (
			<div {...extraProps}>
				{ children }
			</div>
		);
	}

	return (
		<Wrapper>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreColumns = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {};
	let noWrap = false;

	const initialColumnWidths = percentToColumn(content);
	if (Array.isArray(initialColumnWidths) && initialColumnWidths.reduce((acc, col) => (col === 8 ? acc + 1 : acc), 0) === 3) {
		noWrap = true;
		customProps.className = [s.container__coreColumns__noWrap];
	}
	const extraProps = generalSettings({ customProps, attrs, template });
	let useFluidContent = false;
	if (props.useFluidContent) useFluidContent = props.useFluidContent;
	const columnDepth = props?.innerColumnsContent ? parseInt(props?.innerColumnsContent, 10) + 1 : 1;
	let mainImageDepth = props?.mainImageDepth || null;

	// If has EM don't touch order.
	// 		If columns are three even columns scroll on mobile.
	// Else make columns fluid (remove the column divs in mobile).
	// 		Remove all coreColumns and coreColumn containers from html.

	// Is fluid = acfDeclartion or acfBenefits or columns in columns.

	// In fluid columns
	//		Media (only 1);
	//		Declaration
	//		Blockquote
	// Benefits index.

	if (!props?.innerColumnsContent) {
		const columnsInnerContentStructured = content && content.reduce((acc, section) => {
			const getTemplateId = (innerSection, depth) => {
				if (!innerSection) return [];
				const id = Object.keys(innerSection)[0];
				if (id === 'coreColumn') {
					return (innerSection.coreColumn.content && innerSection.coreColumn.content.map((iIS) => getTemplateId(iIS, depth))) || [];
				}
				if (id === 'coreColumns') {
					depth += 1;
					return ['coreColumns', ...flatten(innerSection.coreColumns.content.map((iIS) => getTemplateId(iIS, depth)))];
				}
				if ((id === 'coreImage' || id === 'coreVideo') && (mainImageDepth === null || depth < mainImageDepth)) {
					mainImageDepth = depth;
				}
				return id;
			};

			return [...acc, ...getTemplateId(section, 1)];
		}, []);
		const columnsInnerContentFlattened = flatten(columnsInnerContentStructured);
		useFluidContent = columnsInnerContentFlattened.filter((tpl) => (tpl === 'acfDeclaration' || tpl === 'acfBenefits' || tpl === 'coreColumns')).length > 0;
	}

	const isNotFluid = props?.innerColumnsContent === undefined || !useFluidContent || (useFluidContent && setup.breakPoint !== 'mobile');

	// Fluid layout remove containers.
	let Wrapper = ({ children }) => children;

	// Keep wrapper if uppermost level useFluidContent is set to false or if true and not mobile.
	if (noWrap) {
		// Horizontal slider
		Wrapper = ({ children }) => (
			<div {...extraProps}>
				<button className={s.container__coreColumns_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__coreColumns_inner}>
					{ children }
				</div>
			</div>
		);
	} else if (isNotFluid) {
		// Not fluid layout
		if (useFluidContent && setup.breakPoint === 'mobile') {
			// Has fluid content
			extraProps.className = `${extraProps.className} ${s.container__coreColumns__fluidContent}`;
		}
		Wrapper = ({ children }) => (
			<div {...extraProps}>
				{ children }
			</div>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} innerColumnsContent={columnDepth} mainImageDepth={mainImageDepth} useFluidContent={useFluidContent} column={props?.column !== undefined && props?.column !== 24 ? false : initialColumnWidths[i]} />;
				})
			}
		</Wrapper>
	);
};

containers.coreGroup = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainer = ({ attrs, content, template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	let Wrapper = ({ children }) => children;

	// Add wrappers
	if (!props.inline && attrs?.align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		Wrapper = ({ children }) => (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<Wrapper>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</Wrapper>
		</ErrorBoundary>
	);
};

const TemplateParser = ({ sections, setup, variantClasses = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			const ChildElement = getTemplate(template, section[template].attrs, setup, false);
			if (ChildElement) {
				const Wrapper = ({ children }) => (classes.length > 0 ? <div className={classes.join(' ')}>{ children }</div> : children);
				const nextContent = section[template];
				templateContents[templateContents.length] = (
					<Wrapper key={nextContent.key}>
						<ChildElement template={template} setup={setup} first={first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</Wrapper>
				);
			}
		}
		first = false;
	});

	return templateContents;
};

export default hot(module)(TemplateParser);
